import React, { useEffect, useState } from 'react';
import { CreateOrUpdateBillingRequest } from '../requests/create.or.update.billing.request';
import { useAppDispatch, useAppSelector } from '../redux/redux.hooks';
import {
  createOrUpdateBilling,
  devBilling,
  getBilling,
  activateBilling,
  resetBilling,
} from '../reducers/billing.reducer';
import { useParams } from 'react-router-dom';

import styled from 'styled-components';
import Spacer from '../components/layout/Spacer';
import { P } from '../components/font/Parargraph';
import Loading from '../components/loading/Loading';
import { useToast } from '../components/toast/ToastProvider';
import { BillingPlans } from '../models/billing';
import { capitalize } from '../utils/string.helper';

const ShopBilling = () => {
  const params = useParams();
  const { id: shop_id } = params;
  const dispatch = useAppDispatch();
  const toast = useToast();
  const {
    data,
    status,
    createOrUpdateStatus,
    devBillingStatus,
    resetBillingStatus,
    activateBillingStatus,
  } = useAppSelector((state) => state.billingReducer);

  const [selected, setSelected] = useState<BillingPlans | undefined>(undefined);
  const [shouldUpdateBilling, setShouldUpdateBilling] = useState(false);
  const [billingRequest, setBillingRequest] = useState<
    CreateOrUpdateBillingRequest | undefined
  >(undefined);

  const handleSelectBilling = (plan: BillingPlans) => {
    setSelected(plan);
    if (!data || data?.shop_billing.billing_plan === plan.id) {
      setShouldUpdateBilling(false);
    } else {
      setShouldUpdateBilling(true);
      const request: CreateOrUpdateBillingRequest = {
        shop_id,
        billing_option: {
          billing_plan: plan.id!,
          name: plan.name,
          price: plan.recurring_price,
        },
        active: true,
      };
      setBillingRequest(request);
    }
  };

  const handleUpdateBilling = () => {
    if (
      billingRequest &&
      billingRequest.billing_option.name !== data?.shop_billing.name
    ) {
      dispatch(createOrUpdateBilling(billingRequest));
    }
  };

  useEffect(() => {
    dispatch(getBilling(shop_id!));
  }, [dispatch, shop_id]);

  useEffect(() => {
    setSelected(
      data?.billing_plans.find((p) => p.id === data.shop_billing.billing_plan)
    );
  }, [data]);

  useEffect(() => {
    if (createOrUpdateStatus === 'successful') {
      setShouldUpdateBilling(false);
      toast.show('Billing updated successfully 💃. Please reload to see changes');
    } else if (createOrUpdateStatus === 'error') {
      toast.show('Failed update the billing 😢', { isError: true });
    }
  }, [createOrUpdateStatus, toast]);

  useEffect(() => {
    if (devBillingStatus === 'successful') {
      toast.show('Dev billing set successfully 💃. Please reload to see changes');
    } else if (devBillingStatus === 'error') {
      toast.show('Failed to set a dev billing 😢', { isError: true });
    }
  }, [devBillingStatus, toast]);

  useEffect(() => {
    if (activateBillingStatus === 'successful') {
      toast.show('Billing has been activated 💃. Please reload to see changes');
    } else if (activateBillingStatus === 'error') {
      toast.show('Failed to set a dev billing 😢', { isError: true });
    }
  }, [activateBillingStatus, toast]);

  useEffect(() => {
    if (resetBillingStatus === 'successful') {
      toast.show('Billing reset successfully 💃. Please reload to see changes');
    } else if (resetBillingStatus === 'error') {
      toast.show('Failed to reset the billing 😢', { isError: true });
    }
  }, [resetBillingStatus, toast]);

  const renderCurrentBilling = () => {
    const shop_billing = data?.shop_billing;
    const billing_option = shop_billing
      ? data?.billing_plans.filter((p) => p.id === shop_billing.billing_plan)[0]
      : undefined;
    return (
      <CurrentBillingCard>
        <CurrentBilling>
          {shop_billing?.name
            ? `[${capitalize(billing_option?.invoiced_via!)}] ${
                shop_billing.name
              }`
            : 'No Billing'}
        </CurrentBilling>
        <Spacer />
        <Spacer height={12} />
        <BillingInfo>${shop_billing?.price ?? '0.00'}</BillingInfo>
        <Spacer height={2} />
        <BillingInfo>
          {shop_billing?.active ? 'Active' : 'Deactivated'}
        </BillingInfo>
      </CurrentBillingCard>
    );
  };

  const renderOptions = () => {
    let plans = data?.billing_plans.filter((p) => p.sort > 0);
    plans?.sort((a, b) => a.sort - b.sort);
    return (
      <OptionsContainerWrapper>
        <OptionsTitle>Plans</OptionsTitle>
        <Spacer />
        <OptionsContainer>
          {plans?.map((plan, index) => {
            return (
              <s-button
                key={`id-${index}`}
                onClick={() => handleSelectBilling(plan)}
                variant={selected?.id === plan.id ? 'primary' : 'secondary'}
              >
                [{capitalize(plan.invoiced_via)}]{plan.name}
              </s-button>
            );
          })}
        </OptionsContainer>
      </OptionsContainerWrapper>
    );
  };

  const renderDevOrResetButtons = () => {
    return (
      <DevOrResetContainer>
        <s-button
          disabled={!!data?.shop_billing?.name}
          onClick={() => dispatch(devBilling(shop_id!))}
          loading={devBillingStatus === 'loading'}
        >
          For Devs
        </s-button>
        <s-button
          disabled={!data?.shop_billing?.active}
          onClick={() => dispatch(resetBilling(shop_id!))}
          loading={resetBillingStatus === 'loading'}
        >
          Reset
        </s-button>
        {!data?.shop_billing?.active && data?.shop_billing.billing_plan && (
          <s-button
            onClick={() => dispatch(activateBilling(shop_id!))}
            loading={activateBillingStatus === 'loading'}
          >
            Activate
          </s-button>
        )}
      </DevOrResetContainer>
    );
  };

  const renderUpdateBillingButton = () => {
    return (
      <>
        {shouldUpdateBilling && (
          <ChangeBillingInfo>
            <P>
              You have selected a different billing option than the current. Do
              you wish to update the current billing?
            </P>
            <Spacer />
            <s-button
              variant='primary'
              loading={createOrUpdateStatus === 'loading'}
              onClick={handleUpdateBilling}
            >
              Update billing
            </s-button>
          </ChangeBillingInfo>
        )}
      </>
    );
  };

  const renderPage = () => {
    if (status === 'loading') {
      return <Loading loadingMessage='Loading the billing info' />;
    }
    return (
      <Container>
        <InnerContainer>
          {renderCurrentBilling()}
          <Spacer height={16} />
          {renderDevOrResetButtons()}
          {renderOptions()}
          <Spacer height={64} />
          {renderUpdateBillingButton()}
        </InnerContainer>
      </Container>
    );
  };

  return renderPage();
};

export default ShopBilling;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 10px 80px;
  align-items: center;
  justify-content: flex-start;
`;

const InnerContainer = styled.div`
  width: 640px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const CurrentBillingCard = styled.div`
  width: 100%;
  height: 50%;
  background: white;
  box-shadow: 4px 4px 12px #ccccccb3;
  padding: 16px;
  border-radius: 12px;
`;

const ChangeBillingInfo = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  width: 100%;
`;

const CurrentBilling = styled.span`
  font-size: 24px;
  font-weight: 600;
`;
const BillingInfo = styled.span`
  font-size: 16px;
`;

const DevOrResetContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OptionsContainerWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const OptionsContainer = styled.div`
  padding: 20px;
  background: #ededed;
  border-radius: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const OptionsTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
`;
